import { darkTheme, useTheme } from '@tableau/vizportalclient-common-components';
import { ButtonType, ButtonWidget, PopoverInstance, PopoverWidget, StatusStickerWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { cssClass } from '~src/style';

export type StatusBadgePopoverAction = Readonly<{
  label: string;
  onClick: () => void;
  testId: string;
  disabled?: boolean;
}>;

export type StatusBadgePopoverProps = Readonly<{
  variant: 'error' | 'warning';
  testId: string;
  describedById: string;
  count: number;
  title: string;
  body: React.ReactNode;
  // Zero or more actions rendered as buttons below the body. A warning row can
  // carry more than one actionable warning at once (e.g. deleted projects and a
  // missing domain-wide rule), so this is a list rather than a single action.
  actions?: ReadonlyArray<StatusBadgePopoverAction>;
  // Element to move focus to when the popover closes. Important when the badge
  // itself may unmount on action success (e.g. the last warning clears), which
  // would otherwise drop focus to document.body (WCAG 2.4.3 / 2.4.7).
  getElementToRefocus?: () => HTMLElement | null;
  // Fired when the badge trigger is activated (e.g. to reveal the rows that
  // conflict with this one).
  onTriggerClick?: () => void;
}>;

const actionsClass = cssClass('routing-filter-status-badge', 'actions', {
  marginTop: TabStyles.Spacing.DefaultSpace,
  display: 'flex',
  // English labels ("Add domain-wide rule", "Clear Deleted Projects") sit side
  // by side within the popover width, but translations can be longer. Allow the
  // second action to wrap to a new row rather than overflowing the popover — the
  // labels stay fully readable in every locale.
  flexWrap: 'wrap',
  gap: TabStyles.Spacing.SmallSpace,
  $nest: {
    // Buttons size to their labels rather than stretching to fill the row, so a
    // lone action (e.g. just "Add domain-wide rule") stays label-width instead
    // of spanning the full popover. minWidth:0 still lets a button shrink below
    // its content width if two long labels would otherwise overflow the row.
    '& > *': { flex: '0 1 auto', minWidth: 0 },
  },
});

export function StatusBadgePopover(props: StatusBadgePopoverProps) {
  const { variant, testId, describedById, count, title, body, actions, getElementToRefocus, onTriggerClick } = props;
  const popoverInstance = React.useRef<PopoverInstance>(null);
  // Drive the sticker's dark-background styling off the live theme so its
  // interactive border stays legible when the user toggles themes. useTheme
  // re-renders on themeChanged (the repo's standard theme-reactivity hook).
  const isDarkTheme = useTheme() === darkTheme;

  return (
    <PopoverWidget
      ref={popoverInstance}
      testId={testId}
      aria-describedby={describedById}
      placement='bottom'
      // Wide enough for the two longest English action labels ("Add domain-wide
      // rule" and "Clear Deleted Projects") to sit side by side on one row;
      // longer localized labels wrap to a second row (see actionsClass) rather
      // than overflowing.
      width={380}
      showTitleBar={true}
      title={title}
      getElementToRefocus={getElementToRefocus}
      popoverContent={
        <div id={describedById}>
          {body}
          {actions && actions.length > 0 && (
            <div className={actionsClass}>
              {actions.map((action) => (
                <ButtonWidget
                  key={action.testId}
                  buttonType={ButtonType.Outline}
                  // High density trims padding so two long-labelled actions fit
                  // side by side within the popover width.
                  density='high'
                  onClick={() => {
                    // Close the popover before running the action. Some actions
                    // (e.g. opening the create dialog) don't unmount this row, so
                    // without this the popover would linger and overlap the dialog.
                    popoverInstance.current?.toggle(false);
                    action.onClick();
                  }}
                  disabled={action.disabled}
                  testId={action.testId}
                >
                  {action.label}
                </ButtonWidget>
              ))}
            </div>
          )}
        </div>
      }
      triggerElement={
        // StatusStickerWidget carries the themed error/warning fill, border, and
        // built-in status icon, so the badge stays legible in both light and dark
        // themes without hand-rolled color overrides. Supplying onClick renders it
        // interactive; PopoverWidget clones the trigger and chains its own toggle
        // ahead of this onClick, so onTriggerClick still fires on activation.
        <StatusStickerWidget
          stickerType={variant}
          onClick={onTriggerClick}
          darkBackground={isDarkTheme}
          aria-label={title}
          // Mirror the severity onto a stable DOM attribute so headless tests can
          // assert error vs. warning directly on the rendered badge, rather than
          // relying only on stickerType (which reaches the DOM as a styling
          // className) or the localized aria-label. Matches the dialog banner's
          // data-severity hook.
          data-severity={variant}
          testId={testId + '-badge'}
        >
          {String(count)}
        </StatusStickerWidget>
      }
    />
  );
}
